import { clients } from '../../util/clients';
import { parseJson, isEmptyObject } from '../../util/shared/vue-global';

const { backendRest } = clients.direct;

export const parseResponse = (response) => {
  let errorResponse;
  const resData = response.data;
  if (resData) {
    const resultData = resData.results;
    if (Array.isArray(resultData) && resultData.length > 0) {
      errorResponse = resultData[0].errors;
    }
  }
  return errorResponse;
};

const isResponseHasSingleElement = (response) => Array.isArray(response) && response.length === 1;

export const parseErrorResult = (response) => {
  const { programType } = response;
  let additionalErrors = {};

  if (!isEmptyObject(response[programType])
    && Object.prototype.hasOwnProperty.call(response[programType], 'data')) {
    additionalErrors = {
      key: programType,
      errors: response[programType].data.length > 0 ? response[programType].data : [],
      proceedImport: response[programType].proceedImport,
    };
  }
  const error = {
    generalErrors: (response.errors || []).length > 0 ? response.errors : [],
    additionalErrors,
    preview: Array.isArray(response.preview) ? response.preview : [],
    hasErrors: !!response.hasErrors,
    hasBadDates: !!response.hasBadDates,
    programType,
  };
  return error;
};

export const parseError = (response) => {
  const errorResponse = parseResponse(response);
  let dialog = false;
  let errorMode;
  let errors = [];
  if (errorResponse) {
    if (isResponseHasSingleElement(errorResponse)) {
      const [jsonResponse, parseStatus] = parseJson(errorResponse);
      errors = jsonResponse;
      if (parseStatus) {
        errorMode = 'errorWithHeader';
        errors = parseErrorResult(errors);
      }
    } else {
      errors = errorResponse;
    }
    dialog = true;
  }
  return [dialog, errors, errorMode];
};

export const uploadPercent = (progEvt) => {
  const { loaded, total } = (progEvt || {});
  if (!total || !loaded) {
    return 0;
  }
  return Math.min(100, Math.round((loaded / total) * 100));
};

// The import route answers in JSON-lines: the destination writes one object per batch as that batch
// lands, and the stringify transform terminates each with a newline. So the newlines in the partial
// response body count the batches the server has finished. A streamed response has no
// content-length, so there is no honest percentage for this phase -- the batch count is the progress.
// Kept as a closure over the last offset read, because the handler fires per chunk and rescanning the
// whole body every time turns this into O(n^2) on a big import.
export const batchCounter = () => {
  let seen = 0;
  let batches = 0;
  return (progEvt) => {
    let text = '';
    try {
      text = ((progEvt || {}).target || {}).responseText || '';
    } catch (e) {
      return batches; // a responseType other than text makes responseText unreadable
    }
    if (text.length < seen) {
      seen = 0;
      batches = 0;
    }
    for (let i = seen; i < text.length; i += 1) {
      if (text[i] === '\n') {
        batches += 1;
      }
    }
    seen = text.length;
    return batches;
  };
};

// Sources whose first line is column names rather than data -- see createSourceToJsonTransforms on
// the backend: 'csv' and 'csv-ignore-header' both parse with noheader:false, the other sources treat
// every line as a row.
const HEADER_SOURCES = ['csv', 'csv-ignore-header'];

export const sourceHasHeader = (source) => HEADER_SOURCES.indexOf(source) > -1;

// json-to-gql.js batches every batchSize rows and pushes one result per batch, falling back to 200
// when the profile does not set its own. Rows + batch size is all it takes to turn the batch count
// the response streams back into a real percentage.
export const DEFAULT_BATCH_SIZE = 200;

export const batchSizeOf = (profileBatchSize) => Number(profileBatchSize) || DEFAULT_BATCH_SIZE;

export const rowsToBatches = (rows, batchSize) => {
  if (!rows || rows < 1) {
    return 0;
  }
  return Math.ceil(rows / batchSizeOf(batchSize));
};

// k finished batches means exactly k*batchSize rows are through, except for the last batch which is
// partial -- hence the clamp to the total.
export const rowsCompleted = (batches, totalRows, batchSize) => {
  if (!batches || batches < 1) {
    return 0;
  }
  return Math.min(totalRows, batches * batchSizeOf(batchSize));
};

// Grouped by thousands so a big import reads at a glance. Done here rather than with toLocaleString
// so the text does not shift with the runtime locale, and not with amountToNumber, which is a
// currency parser that strips the separators back out again.
export const groupThousands = (value) => `${Math.round(Number(value) || 0)}`
  .replace(/\B(?=(\d{3})+(?!\d))/g, ',');

const LF = 10;
const CR = 13;
const SLICE_BYTES = 4 * 1024 * 1024;

// off window rather than the bare global: same object in the browser, and the unit test environment
// only exposes it there
const readSliceBytes = (blob) => new Promise((resolve, reject) => {
  const reader = new window.FileReader();
  reader.onload = () => resolve(new Uint8Array(reader.result));
  reader.onerror = () => reject(reader.error || new Error('Could not read the file'));
  reader.readAsArrayBuffer(blob);
});

// Counts the rows the import will produce, by scanning bytes rather than decoding text: 0x0A never
// appears inside a multi-byte UTF-8 character, so a slice boundary cannot split a newline and there
// is no decode cost. Blank lines are not counted, because csvtojson emits no row for them. Read a
// slice at a time so a large file never lands in memory whole.
export const countFileRows = async (file, { hasHeader = false } = {}) => {
  const canRead = (typeof window !== 'undefined') && !!window.FileReader;
  if (!file || !file.size || (typeof file.slice !== 'function') || !canRead) {
    return 0;
  }
  let lines = 0;
  let lineHasContent = false;
  for (let start = 0; start < file.size; start += SLICE_BYTES) {
    const end = Math.min(start + SLICE_BYTES, file.size);
    // deliberately sequential -- the point is to hold one slice at a time
    // eslint-disable-next-line no-await-in-loop
    const bytes = await readSliceBytes(file.slice(start, end));
    for (let i = 0; i < bytes.length; i += 1) {
      const byte = bytes[i];
      if (byte === LF) {
        if (lineHasContent) {
          lines += 1;
        }
        lineHasContent = false;
      } else if (byte !== CR) {
        lineHasContent = true;
      }
    }
  }
  if (lineHasContent) {
    lines += 1;
  }
  return (hasHeader && lines > 0) ? lines - 1 : lines;
};

export const filePreValidate = async (params) => {
  const {
    form,
    formHeaders,
    onUploadProgress,
    onDownloadProgress,
  } = params;
  const response = await backendRest.post('/transmap/import', form, {
    headers: formHeaders,
    onUploadProgress,
    onDownloadProgress,
  });

  const [dialog, errors, errorMode] = parseError(response);
  return [dialog, errors, errorMode];
};

export const importFileProgramMapperWithBackup = ({
  OD: 'odtransactions',
  WAR: 'wartransactions',
  PAYROLL: 'payrolltransactions',
  MISC: 'misctransactions',
  MTG: 'mtgtransactions',
});

export default {};
